import { CheckboxGroup, Collapse, CollapseProps, freddyTheme } from '@coolblue-development/freddy';
import selectMoreLessFiltersTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentFilter/selectMoreLessFiltersTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useTranslation } from '@i18n/useTranslation';
import {
  collapsableFilterCategories,
  FilterCategories,
  FilterOption
} from '@server/careers-backend/search';
import React from 'react';

import { CheckboxItem } from './CheckboxItem';

type Props = {
  label: string | undefined;
  filterCategory: FilterCategories;
  filterOptions: FilterOption<string>[];
  isMobileVersion: boolean;
  isBoolean?: boolean;
  selectedFilterOptions: string[];
  onChange: (values: string[]) => void;
};

export const FilterFacetCheckboxItem = ({
  label,
  filterCategory,
  filterOptions,
  isMobileVersion,
  isBoolean = false,
  selectedFilterOptions,
  onChange
}: Props) => {
  const { t } = useTranslation('filterPage.filterFacets');
  const { trackEvent } = useAnalytics();

  const booleanYesOption = {
    count: 0,
    id: '1',
    filterKey: 'true',
    displayName: t('Ja'),
    hideCount: true
  };

  const shownOptions = filterOptions
    .slice(0, 5)
    .map(option => {
      if (isBoolean) {
        if (String(option.filterKey) !== '1' && option.filterKey !== 'true') {
          return null;
        }

        return { ...booleanYesOption, count: option.count, hideCount: false };
      }

      return option;
    })
    .filter(option => option !== null);

  if (isBoolean && !shownOptions.find(option => option.filterKey === 'true')) {
    shownOptions.unshift(booleanYesOption);
  }

  const collapsedOptions = filterOptions.slice(5);

  const shouldCollapse =
    !isMobileVersion &&
    collapsableFilterCategories.includes(filterCategory) &&
    collapsedOptions.length > 1;

  const subFeature = isMobileVersion ? 'filter_overlay' : 'filter_list';

  const CollapseWrapper = (props: CollapseProps) =>
    shouldCollapse ? <Collapse {...props} /> : <React.Fragment>{props.children}</React.Fragment>;

  return (
    <CheckboxGroup
      label={label}
      value={selectedFilterOptions}
      onChange={onChange}
      sx={{
        legend: {
          ...freddyTheme.typography.h3,
          marginBottom: `${freddyTheme.spacing[200]} !important`
        }
      }}
    >
      {shownOptions.map(item => (
        <CheckboxItem
          key={item.filterKey}
          option={item}
          filterCategory={filterCategory}
          subFeature={subFeature}
        />
      ))}
      <CollapseWrapper
        togglePosition='top'
        translations={{
          labelCollapsed: t('showMore', { count: collapsedOptions.length }),
          labelExpanded: t('showLess')
        }}
        sx={{
          div: { display: 'flex', flexDirection: 'column', gap: freddyTheme.spacing[100] }
        }}
        onToggle={isExpanded =>
          trackEvent(
            selectMoreLessFiltersTrigger({
              sub_feature: subFeature,
              property_name: filterCategory,
              method: isExpanded ? 'select_more' : 'select_less'
            })
          )
        }
      >
        {collapsedOptions.map(item => (
          <CheckboxItem
            key={item.id}
            option={item}
            filterCategory={filterCategory}
            subFeature={subFeature}
          />
        ))}
      </CollapseWrapper>
    </CheckboxGroup>
  );
};
